import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, X } from "lucide-react";
import { useTenant } from "@/tenant/TenantProvider";
import { aiHintDismissedKey } from "@/lib/storageKeys";

/**
 * "Let the AI build it" — offered where a guest is demonstrably undecided.
 *
 * The Auto Plate used to exist on exactly one screen: a guest who tapped BUILD
 * MY PLATE never met it again, however long they scrolled. This is the exit ramp
 * from that, and it is deliberately a line of text with a dismiss button rather
 * than anything that interrupts reading — a banner nobody can close is the point
 * where a suggestion becomes a nuisance.
 *
 * Silent when the restaurant has the AI switched off.
 */
const AiHint = ({ onAuto, testId }) => {
  const { slug, restaurant } = useTenant();
  const { t } = useTranslation();
  const storageKey = aiHintDismissedKey(slug);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(storageKey) === "1";
    } catch (e) {
      return false;
    }
  });

  if (dismissed || restaurant?.ai?.enabled === false) return null;

  const hide = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(storageKey, "1");
    } catch (e) {
      // a browser that refuses storage still gets a hint it can close
    }
  };

  return (
    <div
      data-testid={testId}
      className="flex items-center gap-3 rounded-2xl border border-[#E34A27]/25 bg-[#E34A27]/[0.06] px-4 py-3"
    >
      <div className="w-9 h-9 rounded-full bg-[#E34A27]/10 flex items-center justify-center shrink-0">
        <Sparkles size={17} className="text-[#E34A27]" />
      </div>
      <button
        data-testid={`${testId}-open`}
        onClick={onAuto}
        className="flex-1 min-w-0 text-left active:opacity-70"
      >
        <div className="text-[13px] font-bold text-neutral-900 leading-tight">
          {t("aiHint.title", { persona: restaurant?.ai?.persona_name || "Fuelly" })}
        </div>
        <div className="text-[11px] text-neutral-600 mt-0.5">{t("aiHint.body")}</div>
      </button>
      <button
        data-testid={`${testId}-dismiss`}
        onClick={hide}
        aria-label={t("common.close")}
        className="w-8 h-8 rounded-full bg-white/70 flex items-center justify-center shrink-0 active:scale-95 transition-transform"
      >
        <X size={14} className="text-neutral-500" />
      </button>
    </div>
  );
};

export default AiHint;
